/**
 * Dealer Conversation Helper
 * 
 * For each type of issue, provides:
 * - What to say to the dealer
 * - Common excuses to NOT accept
 * - What you SHOULD accept
 * - Escalation steps
 */

export const DEALER_RESPONSES = {
  // Paint & Body Issues
  'paint_scratch': {
    issue: 'Scratches on paint',
    sayThis: [
      'I noticed scratches on [specific panel]. This needs to be addressed before I can accept delivery.',
      'Please provide written acknowledgment of this paint defect.',
      'I\'d like this to be repainted/polished to showroom condition before delivery.',
    ],
    dontAccept: [
      '"It will be polished during first service" - ❌ Get it fixed now',
      '"This is normal from transport" - ❌ It\'s NOT normal',
      '"We\'ll touch it up, no problem" - ❌ Touch-up ≠ proper repair',
      '"You won\'t notice it after some time" - ❌ You\'re paying for new',
    ],
    acceptable: [
      '✅ Written acknowledgment with photo attached',
      '✅ Immediate repair at dealership (watch the process)',
      '✅ Panel replacement if scratches are deep',
      '✅ Reasonable discount with written documentation',
    ],
    escalation: 'Request to speak with the Service Manager or Dealer Principal.',
  },
  
  'paint_mismatch': {
    issue: 'Paint color mismatch between panels',
    sayThis: [
      'The color on [panel] doesn\'t match the adjacent panels. Has this car been repainted?',
      'I need a written statement confirming whether any panel has been repainted.',
      'This is unacceptable for a brand new car. I\'d like a different unit.',
    ],
    dontAccept: [
      '"It\'s lighting difference" - ❌ Check in daylight from multiple angles',
      '"All cars have slight variation" - ❌ Not within the same car',
      '"It\'s factory variation" - ❌ Factory QC doesn\'t allow this',
    ],
    acceptable: [
      '✅ Replacement with another unit',
      '✅ Significant discount with full disclosure in writing',
      '✅ Written certification that no accident/repair occurred',
    ],
    escalation: 'This is a serious red flag. Consider rejecting delivery entirely.',
    severity: 'critical',
  },
  
  'panel_gap': {
    issue: 'Uneven panel gaps',
    sayThis: [
      'The panel gaps are uneven. This suggests assembly or repair issues.',
      'Can you confirm this car hasn\'t been in an accident?',
      'I\'d like this inspected and documented.',
    ],
    dontAccept: [
      '"This is within tolerance" - ❌ Compare with display unit',
      '"We can adjust it" - ❌ Panel adjustment suggests deeper issues',
    ],
    acceptable: [
      '✅ Comparison with another unit to verify',
      '✅ Written statement about vehicle history',
    ],
    escalation: 'Consider a professional inspection if gaps are significantly uneven.',
  },
  
  'dent_damage': {
    issue: 'Dents or body damage',
    sayThis: [
      'There\'s a visible dent on [location]. This must be fixed before delivery.',
      'I want PDR (Paintless Dent Repair) if the paint is intact, otherwise full repair.',
      'Please document this issue officially.',
    ],
    dontAccept: [
      '"It\'s very minor" - ❌ You\'re paying for perfect condition',
      '"We\'ll fix it later" - ❌ Get it fixed NOW',
    ],
    acceptable: [
      '✅ Professional PDR done while you wait',
      '✅ Panel replacement if dent is severe',
      '✅ Written acknowledgment if you choose to accept as-is',
    ],
  },
  
  // Mechanical Issues
  'engine_noise': {
    issue: 'Unusual engine noise',
    sayThis: [
      'I\'m hearing an unusual sound from the engine. This needs to be inspected.',
      'I won\'t accept delivery until a technician confirms this is normal.',
      'Please provide a written report on the engine inspection.',
    ],
    dontAccept: [
      '"All new engines sound like that" - ❌ Compare with another unit',
      '"It will settle after break-in" - ❌ Unusual noises should be checked',
      '"It\'s just the injectors" - ❌ Get it documented anyway',
    ],
    acceptable: [
      '✅ Technician inspection report in writing',
      '✅ Test drive of identical unit for comparison',
      '✅ Extended warranty if concern remains',
    ],
    severity: 'critical',
  },
  
  'fluid_leak': {
    issue: 'Fluid leaks observed',
    sayThis: [
      'I can see [fluid type] leaking. This needs immediate attention.',
      'This car should not have ANY leaks as a new vehicle.',
      'I need this diagnosed and repaired with documentation.',
    ],
    dontAccept: [
      '"It\'s just condensation" - ❌ AC condensation is water only, not oil',
      '"It\'s from the protective coating" - ❌ Doesn\'t explain oil/coolant',
    ],
    acceptable: [
      '✅ Complete repair with written report',
      '✅ Replacement unit if leak source is major component',
    ],
    severity: 'critical',
  },
  
  // Electrical Issues
  'electrical_fault': {
    issue: 'Electrical system issues',
    sayThis: [
      'The [system] isn\'t working properly. This needs to be fixed.',
      'Please reset/update the software and demonstrate it working.',
      'I need written confirmation this is resolved.',
    ],
    dontAccept: [
      '"Software will be updated in service" - ❌ Get it done now',
      '"It\'s a known issue with a fix coming" - ❌ Don\'t be a beta tester',
    ],
    acceptable: [
      '✅ Immediate software update/fix',
      '✅ Component replacement if hardware fault',
      '✅ Written acknowledgment with timeline if OTA fix is coming',
    ],
  },
  
  'ac_issue': {
    issue: 'AC not cooling properly',
    sayThis: [
      'The AC isn\'t cooling as expected. Please check the refrigerant and system.',
      'I need the AC to cool to [X] degrees within [Y] minutes.',
      'This must be fixed before I accept delivery.',
    ],
    dontAccept: [
      '"It takes time in hot weather" - ❌ It should cool quickly',
      '"Run it for a few more minutes" - ❌ Test for adequate time',
    ],
    acceptable: [
      '✅ Refrigerant top-up with leak check',
      '✅ Written AC performance test results',
    ],
    severity: 'high',
  },
  
  // Tyre Issues
  'old_tyres': {
    issue: 'Tyres older than 6 months',
    sayThis: [
      'These tyres were manufactured [X] months ago. I\'d like fresher tyres.',
      'Old tyres can have degraded rubber even if unused.',
      'Please replace with tyres manufactured within the last 3 months.',
    ],
    dontAccept: [
      '"Tyres are fine up to 5 years" - ❌ For a NEW car, freshness matters',
      '"We\'ll replace them in first service" - ❌ Get it now',
    ],
    acceptable: [
      '✅ Tyre replacement with recent manufacture date',
      '✅ Documented discount acknowledging old tyres',
    ],
  },
  
  'tyre_damage': {
    issue: 'Tyre sidewall damage or bulge',
    sayThis: [
      'There\'s damage/bulge on this tyre. It\'s a safety hazard.',
      'This tyre must be replaced before delivery. Non-negotiable.',
      'Please show me the new tyre being fitted.',
    ],
    dontAccept: [
      '"It\'s cosmetic" - ❌ Sidewall damage is NEVER cosmetic',
      '"It\'s within specification" - ❌ Bulges are dangerous',
    ],
    acceptable: [
      '✅ Immediate tyre replacement',
    ],
    severity: 'critical',
  },
  
  // Document Issues
  'document_missing': {
    issue: 'Missing documents',
    sayThis: [
      'I need [specific document] before I can accept delivery.',
      'All statutory documents must be provided as per regulations.',
      'Please provide a timeline and acknowledgment for pending documents.',
    ],
    dontAccept: [
      '"We\'ll send it later" - ❌ Get it in writing with date',
      '"You don\'t need that document" - ❌ If required, demand it',
    ],
    acceptable: [
      '✅ All documents provided at delivery',
      '✅ Written acknowledgment with specific date for pending docs',
    ],
  },
  
  'vin_mismatch': {
    issue: 'VIN/Engine number mismatch',
    sayThis: [
      'The VIN doesn\'t match across documents. This must be resolved immediately.',
      'I cannot accept a car with mismatched identification numbers.',
      'Please explain this discrepancy in writing.',
    ],
    dontAccept: [
      '"It\'s a typo" - ❌ Demand correction and verification',
      '"We\'ll fix it in RC" - ❌ Wrong VIN is a legal nightmare',
    ],
    acceptable: [
      '✅ Full verification and correction of all documents',
      '✅ Different unit if mismatch cannot be explained',
    ],
    severity: 'critical',
  },
  
  // Odometer Issues
  'high_odometer': {
    issue: 'High odometer reading',
    sayThis: [
      'This car shows [X] km. That\'s too high for a new car.',
      'I\'d expect under 50 km. What explains this reading?',
      'I need documentation of why the odometer is this high.',
    ],
    dontAccept: [
      '"It\'s from quality testing" - ❌ QC doesn\'t need 100+ km',
      '"We drove it for your preparation" - ❌ That\'s excessive',
    ],
    acceptable: [
      '✅ Written explanation with discount',
      '✅ Extended warranty to compensate',
      '✅ Different unit with lower reading',
    ],
  },
  
  // General
  'generic_defect': {
    issue: 'General defect found',
    sayThis: [
      'I\'ve found an issue with [item]. This needs to be addressed.',
      'As a new car buyer, I expect everything to be perfect.',
      'Please fix this and document the repair.',
    ],
    dontAccept: [
      '"It\'s normal" - ❌ If you feel it\'s not normal, push back',
      '"No one else complained" - ❌ Your standards matter',
    ],
    acceptable: [
      '✅ Repair completed satisfactorily',
      '✅ Written acknowledgment of issue',
      '✅ Appropriate compensation if applicable',
    ],
  },
};

/**
 * Get dealer response guide for an issue type
 */
export const getDealerResponse = (issueType) => {
  return DEALER_RESPONSES[issueType] || DEALER_RESPONSES['generic_defect'];
};

/**
 * Get all response categories
 */
export const getResponseCategories = () => {
  return Object.entries(DEALER_RESPONSES).map(([key, value]) => ({
    id: key,
    ...value,
  }));
};

/**
 * Map checklist item to dealer response
 */
export const mapItemToResponse = (itemId, itemTitle) => {
  const lowerTitle = itemTitle.toLowerCase();
  
  if (lowerTitle.includes('paint') && lowerTitle.includes('scratch')) return 'paint_scratch';
  if (lowerTitle.includes('paint') && (lowerTitle.includes('color') || lowerTitle.includes('mismatch'))) return 'paint_mismatch';
  if (lowerTitle.includes('panel') && lowerTitle.includes('gap')) return 'panel_gap';
  if (lowerTitle.includes('dent')) return 'dent_damage';
  if (lowerTitle.includes('engine') && (lowerTitle.includes('noise') || lowerTitle.includes('sound'))) return 'engine_noise';
  if (lowerTitle.includes('leak')) return 'fluid_leak';
  if (lowerTitle.includes('ac') || lowerTitle.includes('air condition')) return 'ac_issue';
  if (lowerTitle.includes('tyre') && lowerTitle.includes('date')) return 'old_tyres';
  if (lowerTitle.includes('tyre') && (lowerTitle.includes('damage') || lowerTitle.includes('sidewall'))) return 'tyre_damage';
  if (lowerTitle.includes('vin') || lowerTitle.includes('engine number')) return 'vin_mismatch';
  if (lowerTitle.includes('odometer')) return 'high_odometer';
  if (lowerTitle.includes('document') || lowerTitle.includes('form')) return 'document_missing';
  if (lowerTitle.includes('electric') || lowerTitle.includes('infotainment')) return 'electrical_fault';
  
  return 'generic_defect';
};

export default DEALER_RESPONSES;
